import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Unstable_Grid2 as Grid, Box, Typography } from '@mui/material';
import AvailabilityAppBarButtons from 'components/availability/AvailabilityAppBarButtons';
import AvailabilityEvent from 'components/availability/AvailabilityEvent';
import AvailabilityRepeatButton from 'components/availability/AvailabilityRepeatButton';
import AvailabilityDeleteAllButton from 'components/availability/AvailabilityDeleteAllButton';
import AvailabilityDeleteWeekButton from 'components/availability/AvailabilityDeleteWeekButton';
import AvailabilitySidebar from 'components/availability/AvailabilitySidebar';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import {
  availabilityEventTimeFormat,
  availabilityHeaderToolbar,
  availabilitySlotDuration,
  availabilitySlotLabelFormat
} from 'common/constants/availability';
import { compactSlotLabelContent } from 'common/constants/session';
import AppBarContext from 'context/AppBarContext';
import LayoutContext from 'context/LayoutContext';
import useAvailabilityCalendar from 'hooks/availability/useAvailabilityCalendar';
import useAvailabilitySidebar from 'hooks/availability/useAvailabilitySidebar';

import 'styles/full-calendar.css';

function AvailabilityCalendar() {
  const { t, i18n } = useTranslation(['availabilities', 'buttons', 'common', 'routes']);
  const pageTitle = t('availability.trainer', { ns: 'routes' });
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const { setCompactLayout, setNoBgColorLayout } = useContext(LayoutContext);

  const availabilities = useSelector((state) => state.availability.data.availabilities);

  const {
    calendarApi,
    calendarRef,
    handleChange,
    handleDateClick,
    handleDateSelect,
    handleDateSet,
    handleDelete,
    selectAllow
  } = useAvailabilityCalendar();

  const { handleSessionsFilter, disableReset, handleResetSessionList, sessionsToShow } =
    useAvailabilitySidebar();

  // Eliminar disponibilidad SIEMPRE pasa por el modal de confirmación compartido.
  const [eventToDelete, setEventToDelete] = useState(null);

  const confirmDeleteEvent = useCallback(() => {
    if (eventToDelete) {
      handleDelete(eventToDelete);
    }
    setEventToDelete(null);
  }, [eventToDelete, handleDelete]);

  useEffect(() => {
    setCompactLayout(true);
    setNoBgColorLayout(true);

    return () => {
      setCompactLayout(false);
      setNoBgColorLayout(false);
    };
  }, []);

  useEffect(() => {
    setAppBarButton(<AvailabilityAppBarButtons />);
    setAppBarTitle(pageTitle);
  }, [setAppBarButton, setAppBarTitle]);

  const renderEventContent = useCallback(
    (info) => (
      <AvailabilityEvent
        event={info.event}
        onDelete={setEventToDelete}
        onFilter={handleSessionsFilter}
      />
    ),
    [handleSessionsFilter]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Grid container spacing={4} disableEqualOverflow>
        <Grid xs={12} lg={3}>
          <AvailabilitySidebar
            disableReset={disableReset}
            resetList={handleResetSessionList}
            sessions={sessionsToShow}
          />
        </Grid>
        <Grid xs={12} lg={9} sx={{ position: 'relative' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, auto)' },
              gap: 1,
              mb: 2,
              position: 'relative',
              width: '100%'
            }}>
            <AvailabilityDeleteAllButton />
            <AvailabilityDeleteWeekButton calendar={calendarApi} />
            <AvailabilityRepeatButton calendar={calendarApi} />
          </Box>
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 1.5, py: 1,
              borderRadius: '10px', border: '1px solid', borderColor: 'hairline.main',
              backgroundColor: 'surface.overlay'
            }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'gold.main', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('tapHint', { ns: 'availabilities', defaultValue: 'Toca una hora para marcar tu disponibilidad. Mantén presionado y arrastra para marcar varias.' })}
            </Typography>
          </Box>
          <div className="fc-availability-calendar">
            <FullCalendar
              ref={calendarRef}
              plugins={[timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              datesSet={handleDateSet}
              firstDay={1}
              events={availabilities}
              eventTimeFormat={availabilityEventTimeFormat}
              eventContent={renderEventContent}
              eventOverlap={false}
              selectable
              selectOverlap={false}
              selectMirror
              selectAllow={selectAllow}
              dateClick={handleDateClick}
              selectLongPressDelay={120}
              eventLongPressDelay={120}
              longPressDelay={120}
              height="auto"
              allDaySlot={false}
              slotDuration={availabilitySlotDuration}
              slotLabelFormat={availabilitySlotLabelFormat}
              slotLabelContent={compactSlotLabelContent}
              slotMinTime="04:00:00"
              slotMaxTime="21:00:00"
              expandRows
              headerToolbar={availabilityHeaderToolbar}
              buttonText={{
                today: t('today', { ns: 'common' }),
                month: t('month', { ns: 'common' }),
                week: t('week', { ns: 'common' }),
                day: t('day', { count: 1, ns: 'common' }),
                list: t('list', { ns: 'common' })
              }}
              locale={i18n.language}
              timeZone="local"
              eventChange={handleChange}
              select={handleDateSelect}
            />
          </div>
        </Grid>
      </Grid>

      {/* Confirmación antes de eliminar una disponibilidad (modal compartido). */}
      <ConfirmDeleteModal
        open={Boolean(eventToDelete)}
        onClose={() => setEventToDelete(null)}
        onConfirm={confirmDeleteEvent}
        title={t('deleteConfirmTitle', { ns: 'availabilities', defaultValue: 'Eliminar disponibilidad' })}
        message={t('deleteConfirmMessage', {
          ns: 'availabilities',
          defaultValue: '¿Seguro que quieres eliminar este horario de disponibilidad?',
        })}
      />
    </>
  );
}

export default AvailabilityCalendar;
